import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Content,
  Header,
  MarkdownContent,
  Page,
  Progress,
} from '@backstage/core-components';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Button,
  Card,
  CardBody,
  FieldLabel,
  Flex,
  Text,
} from '@backstage/ui';

import styles from './AgentChatPage.module.css';

const SESSION_STORAGE_KEY = 'ps_agent_chat_session_id';

type ChatRole = 'user' | 'assistant';

interface AgentActivityStep {
  kind: string;
  round?: number;
  name?: string;
  text?: string;
  tools?: Array<{ name: string; arguments_preview?: string }>;
}

interface Turn {
  role: ChatRole;
  content: string;
  latencyMs?: number;
  stage?: string;
  debug?: Record<string, unknown>;
  observability?: Array<Record<string, unknown>>;
  activity?: AgentActivityStep[];
}

interface DispatchSummary {
  template_id?: string;
  environment_name?: string;
  verify_status?: string;
  verify_job_id?: string;
  workflow_run_url?: string;
  workflow_conclusion?: string;
}

function readStoredSessionId(): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  const value = sessionStorage.getItem(SESSION_STORAGE_KEY)?.trim();
  return value || null;
}

function writeStoredSessionId(sessionId: string): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
}

function clearStoredSessionId(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }
}

function verifyStatusLabel(status?: string): string {
  const normalized = (status ?? '').toLowerCase();
  if (normalized === 'verified') {
    return 'Ready';
  }
  if (
    normalized === 'awaiting_workflow' ||
    normalized === 'polling' ||
    normalized === 'catalog_check'
  ) {
    return 'In progress';
  }
  if (normalized === 'workflow_failed') {
    return 'Failed';
  }
  if (normalized === 'catalog_timeout') {
    return 'Catalog pending';
  }
  if (normalized === 'dispatched') {
    return 'Dispatched';
  }
  return status?.trim() || 'Unknown';
}

function isVerifyInProgress(status?: string): boolean {
  const normalized = (status ?? '').toLowerCase();
  return (
    normalized === 'awaiting_workflow' ||
    normalized === 'polling' ||
    normalized === 'catalog_check' ||
    normalized === 'dispatched'
  );
}

function extractObservability(
  data: Record<string, unknown>,
): Array<Record<string, unknown>> | undefined {
  const obs = data.observability;
  if (!Array.isArray(obs)) {
    return undefined;
  }
  return obs.filter(
    (row): row is Record<string, unknown> =>
      typeof row === 'object' && row !== null,
  );
}

function extractDebugPayload(
  data: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const dbg = data.debug;
  if (typeof dbg !== 'object' || dbg === null || Array.isArray(dbg)) {
    return undefined;
  }
  return dbg as Record<string, unknown>;
}

interface DebugStep {
  label: string;
  ms: number | null;
  detail?: string;
}

function formatTokenCount(n: number): string {
  if (!Number.isFinite(n) || n < 0) {
    return '';
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}k`;
  }
  return String(Math.round(n));
}

function formatUsageInline(usage: Record<string, unknown>): string | undefined {
  const prompt = usage.prompt_tokens;
  const completion = usage.completion_tokens;
  if (typeof prompt !== 'number' && typeof completion !== 'number') {
    return undefined;
  }
  const parts: string[] = [];
  if (typeof prompt === 'number') {
    parts.push(`${formatTokenCount(prompt)} in`);
  }
  if (typeof completion === 'number') {
    parts.push(`${formatTokenCount(completion)} out`);
  }
  return parts.join(' / ');
}

function buildDebugSteps(debug: Record<string, unknown>): DebugStep[] {
  const steps: DebugStep[] = [];
  const rounds = debug.rounds;
  if (!Array.isArray(rounds)) {
    return steps;
  }
  for (const round of rounds) {
    if (typeof round !== 'object' || round === null) {
      continue;
    }
    const r = round as Record<string, unknown>;
    const roundNum = r.round ?? '?';
    if (typeof r.llm_ms === 'number') {
      const usage =
        typeof r.usage === 'object' && r.usage !== null
          ? formatUsageInline(r.usage as Record<string, unknown>)
          : undefined;
      steps.push({
        label: `Round ${roundNum} · LLM`,
        ms: r.llm_ms,
        detail: usage,
      });
    }
    const execs = r.executions;
    if (!Array.isArray(execs)) {
      continue;
    }
    for (const ex of execs) {
      if (typeof ex !== 'object' || ex === null) {
        continue;
      }
      const e = ex as Record<string, unknown>;
      const name = String(e.name ?? 'tool');
      const blocked = e.blocked === true;
      const ms = typeof e.ms === 'number' ? e.ms : null;
      const result =
        typeof e.result === 'object' && e.result !== null
          ? (e.result as Record<string, unknown>)
          : undefined;
      let detail: string | undefined;
      if (result?.templates_count != null) {
        detail = `${result.templates_count} templates`;
      } else if (result?.chars != null && typeof result.chars === 'number') {
        detail = `${result.chars} chars`;
      }
      steps.push({
        label: blocked
          ? `Round ${roundNum} · ${name} (blocked)`
          : `Round ${roundNum} · ${name}`,
        ms: blocked ? 0 : ms,
        detail,
      });
    }
  }
  return steps;
}

function buildTuningSummary(
  stage: string | undefined,
  debug: Record<string, unknown>,
): string[] {
  const line1: string[] = [];
  if (stage) {
    line1.push(stage);
  }
  const stageStart = debug.provision_stage_start;
  const stageEnd = debug.provision_stage_end;
  if (
    typeof stageStart === 'string' &&
    typeof stageEnd === 'string' &&
    stageStart !== stageEnd
  ) {
    line1.push(`${stageStart}→${stageEnd}`);
  }
  const timing = debug.timing;
  if (typeof timing === 'object' && timing !== null) {
    const t = timing as Record<string, unknown>;
    const bits: string[] = [];
    if (typeof t.total_ms === 'number') {
      bits.push(`${formatLatency(t.total_ms)} total`);
    }
    if (typeof t.llm_ms === 'number') {
      bits.push(`LLM ${formatLatency(t.llm_ms)}`);
    }
    if (typeof t.tools_ms === 'number' && t.tools_ms > 0) {
      bits.push(`tools ${formatLatency(t.tools_ms)}`);
    }
    if (bits.length > 0) {
      line1.push(bits.join(' · '));
    }
  }
  const tokenUsage = debug.token_usage;
  if (typeof tokenUsage === 'object' && tokenUsage !== null) {
    const u = formatUsageInline(tokenUsage as Record<string, unknown>);
    if (u) {
      line1.push(`tokens ${u}`);
    }
  }

  const line2: string[] = [];
  const cacheSummary = debug.cache_summary;
  if (typeof cacheSummary === 'string' && cacheSummary.trim()) {
    line2.push(`cache ${cacheSummary}`);
  }
  const activeTid = debug.active_template_id;
  const activeAtStart = debug.active_template_id_at_start;
  if (typeof activeTid === 'string' && activeTid) {
    line2.push(`template ${activeTid}`);
  } else if (typeof activeAtStart === 'string' && activeAtStart) {
    line2.push(`template (cleared from ${activeAtStart})`);
  }
  const ctx = debug.context;
  if (typeof ctx === 'object' && ctx !== null) {
    const c = ctx as Record<string, unknown>;
    const sys = typeof c.system_chars === 'number' ? c.system_chars : 0;
    const prior =
      typeof c.prior_turn_pairs === 'number' ? c.prior_turn_pairs : 0;
    if (sys >= 12000 || prior >= 4) {
      line2.push(
        `ctx sys ${formatTokenCount(sys)} chars · ${prior} prior turns`,
      );
    }
  }
  const model = debug.model;
  if (typeof model === 'string' && model) {
    line2.push(model);
  }

  const out = [line1.filter(Boolean).join(' · ')].filter(Boolean);
  if (line2.length > 0) {
    out.push(line2.join(' · '));
  }
  return out;
}

function formatAgentStepLabel(step: AgentActivityStep): string {
  const round =
    typeof step.round === 'number' ? `Round ${step.round} · ` : '';
  switch (step.kind) {
    case 'llm':
      return `${round}Thinking…`;
    case 'tool_plan': {
      const tools = step.tools ?? [];
      if (tools.length === 0) {
        return `${round}Planning tools…`;
      }
      const names = tools.map(t => {
        if (t.arguments_preview) {
          const preview =
            t.arguments_preview.length > 80
              ? `${t.arguments_preview.slice(0, 77)}…`
              : t.arguments_preview;
          return `${t.name}(${preview})`;
        }
        return t.name;
      });
      return `${round}Planning: ${names.join(', ')}`;
    }
    case 'tool_run':
      return `${round}Running ${step.name ?? 'tool'}…`;
    case 'assistant_note':
      return step.text ? `${round}${step.text}` : `${round}Assistant note`;
    default:
      return `${round}${step.kind}`;
  }
}

function AgentActivityLog({
  steps,
  compact,
}: {
  steps: AgentActivityStep[];
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (steps.length === 0) {
    return null;
  }
  if (compact && !open) {
    return (
      <div className={styles.activityWrap}>
        <Button size="small" variant="tertiary" onPress={() => setOpen(true)}>
          Activity ({steps.length} steps)
        </Button>
      </div>
    );
  }
  return (
    <div className={styles.activityWrap}>
      {compact && (
        <Button
          size="small"
          variant="tertiary"
          onPress={() => setOpen(false)}
          style={{ marginBottom: 4 }}
        >
          Hide activity
        </Button>
      )}
      <ul className={styles.activitySteps}>
        {steps.map((step, idx) => (
          <li key={`${step.kind}-${idx}`} className={styles.activityStepRow}>
            {formatAgentStepLabel(step)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AgentDebugTrace({
  stage,
  debug,
}: {
  stage?: string;
  debug?: Record<string, unknown>;
}) {
  const [open, setOpen] = useState(false);
  if (!debug) {
    return null;
  }
  const steps = buildDebugSteps(debug);
  const summaryLines = buildTuningSummary(stage, debug);
  return (
    <div className={styles.debugWrap}>
      {summaryLines.map(line => (
        <Text key={line} variant="body-x-small" color="secondary" as="p">
          {line}
        </Text>
      ))}
      {steps.length > 0 && (
        <ul className={styles.debugSteps}>
          {steps.map((step, idx) => (
            <li key={`${step.label}-${idx}`} className={styles.debugStepRow}>
              {step.label}
              {step.ms != null && step.ms >= 0
                ? ` — ${formatLatency(step.ms)}`
                : ''}
              {step.detail ? ` · ${step.detail}` : ''}
            </li>
          ))}
        </ul>
      )}
      <Button
        size="small"
        variant="tertiary"
        onPress={() => setOpen(v => !v)}
        style={{ marginTop: 4 }}
      >
        {open ? 'Hide raw JSON' : 'Raw JSON'}
      </Button>
      {open && (
        <pre className={styles.debugPre}>{JSON.stringify(debug, null, 2)}</pre>
      )}
    </div>
  );
}

function formatLatency(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) {
    return '';
  }
  if (ms < 1000) {
    return `${Math.round(ms)} ms`;
  }
  return `${(ms / 1000).toFixed(1)} s`;
}

function parseSseEvents(buffer: string): {
  events: Array<{ event: string; data: string }>;
  rest: string;
} {
  const events: Array<{ event: string; data: string }> = [];
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? '';
  for (const block of parts) {
    if (!block.trim()) continue;
    let event = 'message';
    let data = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      if (line.startsWith('data:')) data += line.slice(5).trim();
    }
    if (data) events.push({ event, data });
  }
  return { events, rest };
}

async function consumeChatStream(
  res: Response,
  onToken: (delta: string) => void,
  onStatus?: (message: string) => void,
  onAgentStep?: (step: AgentActivityStep) => void,
): Promise<Record<string, unknown>> {
  const reader = res.body?.getReader();
  if (!reader) {
    throw new Error('Streaming not supported by the browser');
  }
  const decoder = new TextDecoder();
  let buffer = '';
  let finalPayload: Record<string, unknown> | null = null;
  const processEvents = (events: Array<{ event: string; data: string }>) => {
    for (const { event, data } of events) {
      if (event === 'token') {
        try {
          const obj = JSON.parse(data) as { delta?: string };
          if (obj.delta) onToken(obj.delta);
        } catch {
          /* ignore malformed token */
        }
      } else if (event === 'status') {
        try {
          const obj = JSON.parse(data) as { message?: string };
          if (obj.message && onStatus) onStatus(obj.message);
        } catch {
          /* ignore malformed status */
        }
      } else if (event === 'agent_step') {
        try {
          const obj = JSON.parse(data) as AgentActivityStep;
          if (obj.kind && onAgentStep) onAgentStep(obj);
        } catch {
          /* ignore malformed agent_step */
        }
      } else if (event === 'done') {
        finalPayload = JSON.parse(data) as Record<string, unknown>;
      } else if (event === 'error') {
        const err = JSON.parse(data) as { message?: string };
        throw new Error(err.message || 'Stream error');
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (value) {
      buffer += decoder.decode(value, { stream: true });
    }
    const parsed = parseSseEvents(done ? `${buffer}\n\n` : buffer);
    buffer = done ? '' : parsed.rest;
    processEvents(parsed.events);
    if (done) break;
  }
  if (!finalPayload) {
    throw new Error('Stream ended without a done event');
  }
  return finalPayload;
}

function extractAssistantText(data: Record<string, unknown>): string {
  for (const key of ['reply', 'message', 'text', 'content', 'answer']) {
    const v = data[key];
    if (typeof v === 'string' && v.trim()) {
      return v;
    }
  }
  return JSON.stringify(data, null, 2);
}

function formatAgentUnreachableError(
  status: number,
  body: string,
  expectedUrl: string,
  isLocalDev: boolean,
): string {
  const raw = body.trim();
  const fromProxy =
    raw.includes('Error occurred while trying to proxy') ||
    status === 504 ||
    status === 502;
  if (fromProxy) {
    if (isLocalDev) {
      return `Nothing is listening at ${expectedUrl} (HTTP ${status}). Open a second terminal and run the PS Agent there — from repo ps-backstage-catalog: ./scripts/start-ps-agent.sh — or: uvicorn ps_backstage_agent.main:app --reload --host 127.0.0.1 --port 8088`;
    }
    if (status === 504) {
      return `PS Agent request timed out (HTTP 504). The agent may still be processing — wait and retry, or ask an admin to check ps-backstage-agent logs on the VM.`;
    }
    return `PS Agent is unreachable (HTTP ${status}). Ask an admin to check the ps-backstage-agent container on the Backstage VM.`;
  }
  return raw || `Request failed (${status})`;
}

export const AgentChatPage = () => {
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [waitingElapsedMs, setWaitingElapsedMs] = useState(0);
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const [streamActivity, setStreamActivity] = useState<AgentActivityStep[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [dispatchSummary, setDispatchSummary] = useState<DispatchSummary | null>(
    null,
  );

  const refreshSessionMeta = useCallback(
    async (sessionId: string) => {
      try {
        const proxyBase = await discoveryApi.getBaseUrl('proxy');
        const identity = await identityApi.getBackstageIdentity();
        const res = await fetchApi.fetch(
          `${proxyBase}/ps-agent/v1/chat/history?session_id=${encodeURIComponent(sessionId)}`,
          {
            headers: { 'X-Agent-User-Ref': identity.userEntityRef },
          },
        );
        if (!res.ok) {
          return;
        }
        const data = (await res.json()) as {
          dispatch_summary?: DispatchSummary | null;
          turns?: Array<{ role: string; content: string }>;
        };
        setDispatchSummary(data.dispatch_summary ?? null);
        const remoteTurns: Turn[] = (data.turns ?? [])
          .filter(
            turn =>
              (turn.role === 'user' || turn.role === 'assistant') &&
              typeof turn.content === 'string' &&
              turn.content.trim(),
          )
          .map(turn => ({
            role: turn.role as ChatRole,
            content: turn.content,
          }));
        if (remoteTurns.length === 0) {
          return;
        }
        setMessages(prev => {
          if (loading) {
            return prev;
          }
          if (remoteTurns.length <= prev.length) {
            return prev;
          }
          // Keep local richer turns (debug/activity); append only new tail from server.
          const appended = remoteTurns.slice(prev.length).map(turn => ({
            role: turn.role,
            content: turn.content,
          }));
          return [...prev, ...appended];
        });
      } catch {
        // Banner / verify notices are best-effort; chat still works without them.
      }
    },
    [discoveryApi, fetchApi, identityApi, loading],
  );

  const startNewChat = useCallback(() => {
    clearStoredSessionId();
    setActiveSessionId(null);
    setMessages([]);
    setDispatchSummary(null);
    setError(undefined);
    if (searchParams.has('session')) {
      const next = new URLSearchParams(searchParams);
      next.delete('session');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    const querySession = searchParams.get('session')?.trim() || null;
    const storedSession = readStoredSessionId();
    const sessionId = querySession || storedSession;
    if (!sessionId) {
      return undefined;
    }

    setHistoryLoading(true);
    (async () => {
      try {
        const proxyBase = await discoveryApi.getBaseUrl('proxy');
        const identity = await identityApi.getBackstageIdentity();
        const res = await fetchApi.fetch(
          `${proxyBase}/ps-agent/v1/chat/history?session_id=${encodeURIComponent(sessionId)}`,
          {
            headers: { 'X-Agent-User-Ref': identity.userEntityRef },
          },
        );
        if (!res.ok) {
          if (!cancelled && res.status === 404) {
            clearStoredSessionId();
            setActiveSessionId(null);
          }
          return;
        }
        const data = (await res.json()) as {
          session_id?: string;
          turns?: Array<{ role: string; content: string }>;
          dispatch_summary?: DispatchSummary | null;
        };
        if (cancelled) {
          return;
        }
        const sid =
          typeof data.session_id === 'string' && data.session_id.trim()
            ? data.session_id.trim()
            : sessionId;
        writeStoredSessionId(sid);
        setActiveSessionId(sid);
        setDispatchSummary(data.dispatch_summary ?? null);
        const restored: Turn[] = (data.turns ?? [])
          .filter(
            turn =>
              (turn.role === 'user' || turn.role === 'assistant') &&
              typeof turn.content === 'string' &&
              turn.content.trim(),
          )
          .map(turn => ({
            role: turn.role as ChatRole,
            content: turn.content,
          }));
        if (restored.length > 0) {
          setMessages(restored);
        }
      } catch {
        // Ignore restore failures; user can still start a fresh chat.
      } finally {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Restore once on mount / when ?session= changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('session')]);

  // Keep session history fresh while a provision is tracked — including after verify
  // reaches a terminal status so "Stack ready" + [LAB_CONTINUE] turns appear without reload.
  // After terminal, keep polling for a few minutes (lab auto-continue), then stop.
  useEffect(() => {
    if (!activeSessionId || !dispatchSummary) {
      return undefined;
    }
    const inProgress = isVerifyInProgress(dispatchSummary.verify_status);
    const startedAt = performance.now();
    const maxTerminalMs = 5 * 60_000;
    const tick = () => {
      if (
        !inProgress &&
        performance.now() - startedAt > maxTerminalMs
      ) {
        return;
      }
      void refreshSessionMeta(activeSessionId);
    };
    tick();
    const id = window.setInterval(tick, inProgress ? 5_000 : 8_000);
    return () => window.clearInterval(id);
  }, [activeSessionId, dispatchSummary?.verify_status, refreshSessionMeta]);

  useEffect(() => {
    if (!loading) {
      setWaitingElapsedMs(0);
      return undefined;
    }
    const start = performance.now();
    const id = window.setInterval(() => {
      setWaitingElapsedMs(Math.round(performance.now() - start));
    }, 100);
    return () => window.clearInterval(id);
  }, [loading]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) {
      return;
    }
    setInput('');
    setError(undefined);

    const history = messages.map(({ role, content }) => ({ role, content }));
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    setStreamStatus(null);
    setStreamActivity([]);
    const clientStartedAt = performance.now();
    const streamAssistantIdx = messages.length + 1;
    const activitySteps: AgentActivityStep[] = [];

    try {
      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const streamUrl = `${proxyBase}/ps-agent/v1/chat/stream`;
      const identity = await identityApi.getBackstageIdentity();
      const sessionId = activeSessionId || readStoredSessionId();

      const body: Record<string, unknown> = { message: text };
      if (sessionId) {
        body.session_id = sessionId;
      } else if (history.length > 0) {
        body.history = history;
      }

      const headers = {
        'Content-Type': 'application/json',
        'X-Agent-User-Ref': identity.userEntityRef,
      };

      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: '', latencyMs: undefined },
      ]);

      let res = await fetchApi.fetch(streamUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!res.ok && res.status !== 404) {
        const errText = await res.text();
        const expectedUrl =
          configApi.getOptionalString('app.psAgent.serviceUrl') ??
          'http://127.0.0.1:8088';
        const isLocal =
          typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1');
        throw new Error(
          formatAgentUnreachableError(
            res.status,
            errText,
            expectedUrl,
            isLocal,
          ),
        );
      }

      let data: Record<string, unknown>;

      if (
        res.ok &&
        res.headers.get('content-type')?.includes('text/event-stream')
      ) {
        data = await consumeChatStream(
          res,
          delta => {
            setStreamStatus(null);
            setMessages(prev => {
              const next = [...prev];
              const row = next[streamAssistantIdx];
              if (row?.role === 'assistant') {
                next[streamAssistantIdx] = {
                  ...row,
                  content: row.content + delta,
                };
              }
              return next;
            });
          },
          message => setStreamStatus(message),
          step => {
            activitySteps.push(step);
            setStreamActivity([...activitySteps]);
          },
        );
      } else {
        const fallbackUrl = `${proxyBase}/ps-agent/v1/chat`;
        res = await fetchApi.fetch(fallbackUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const errText = await res.text();
          const expectedUrl =
            configApi.getOptionalString('app.psAgent.serviceUrl') ??
            'http://127.0.0.1:8088';
          const isLocal =
            typeof window !== 'undefined' &&
            (window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1');
          throw new Error(
            formatAgentUnreachableError(
              res.status,
              errText,
              expectedUrl,
              isLocal,
            ),
          );
        }
        data = (await res.json()) as Record<string, unknown>;
      }

      const sid = data.session_id;
      if (typeof sid === 'string' && sid.trim()) {
        writeStoredSessionId(sid);
        setActiveSessionId(sid);
        void refreshSessionMeta(sid);
      }

      const replyText = extractAssistantText(data);
      const clientRttMs = Math.round(performance.now() - clientStartedAt);
      const serverMsRaw = data.latency_ms;
      const serverMs =
        typeof serverMsRaw === 'number' && Number.isFinite(serverMsRaw)
          ? Math.max(0, serverMsRaw)
          : undefined;
      const latencyMs = serverMs ?? clientRttMs;
      const stage =
        typeof data.stage === 'string' && data.stage.trim()
          ? data.stage
          : undefined;
      const debug = extractDebugPayload(data);
      const observability = extractObservability(data);
      setMessages(prev => {
        const next = [...prev];
        const row = next[streamAssistantIdx];
        if (row?.role === 'assistant') {
          next[streamAssistantIdx] = {
            role: 'assistant',
            content: replyText,
            latencyMs,
            stage,
            debug,
            observability,
            activity: activitySteps.length > 0 ? [...activitySteps] : undefined,
          };
        } else {
          next.push({
            role: 'assistant',
            content: replyText,
            latencyMs,
            stage,
            debug,
            observability,
            activity: activitySteps.length > 0 ? [...activitySteps] : undefined,
          });
        }
        return next;
      });
    } catch (e) {
      setMessages(prev =>
        prev.filter((_, i) => i !== streamAssistantIdx || prev[i]?.content),
      );
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
      setStreamStatus(null);
      setStreamActivity([]);
    }
  }, [
    activeSessionId,
    configApi,
    discoveryApi,
    fetchApi,
    identityApi,
    input,
    loading,
    messages,
    refreshSessionMeta,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <Page themeId="tool">
      <Header
        title="PS Agent"
        subtitle="Chat with the Professional Services agent"
      />
      <Content>
        <div className={styles.layout}>
          <div className={styles.toolbar}>
            <Button variant="secondary" onPress={startNewChat}>
              New chat
            </Button>
          </div>
          {error && (
            <Text variant="body-medium" color="danger" as="p">
              {error}
            </Text>
          )}
          {historyLoading && (
            <Text variant="body-x-small" color="secondary" as="p">
              Restoring conversation…
            </Text>
          )}
          {dispatchSummary && (
            <Card className={styles.provisionBanner}>
              <CardBody>
                <div className={styles.provisionBannerRow}>
                  <div>
                    <Text variant="body-x-small" color="secondary" as="p">
                      Last provision
                    </Text>
                    <Text variant="body-medium" as="p">
                      {(dispatchSummary.template_id || 'stack').trim()}
                      {dispatchSummary.environment_name
                        ? ` · ${dispatchSummary.environment_name}`
                        : ''}
                      {' · '}
                      <strong>
                        {verifyStatusLabel(dispatchSummary.verify_status)}
                      </strong>
                    </Text>
                  </div>
                  <div className={styles.provisionBannerActions}>
                    {dispatchSummary.workflow_run_url && (
                      <Button
                        variant="secondary"
                        onPress={() =>
                          window.open(
                            dispatchSummary.workflow_run_url,
                            '_blank',
                            'noopener,noreferrer',
                          )
                        }
                      >
                        Workflow
                      </Button>
                    )}
                    {activeSessionId && (
                      <Button
                        variant="secondary"
                        onPress={() => void refreshSessionMeta(activeSessionId)}
                      >
                        Refresh status
                      </Button>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
          {messages.length > 0 &&
            !messages.some(m => m.role === 'assistant' && m.debug) && (
              <Text
                variant="body-x-small"
                color="secondary"
                as="p"
                className={styles.debugHint}
              >
                Debug trace appears under each reply when{' '}
                <code>DEBUG_AGENT=true</code> in ps-backstage-agent/.env
                (restart the agent after changing).
              </Text>
            )}
          <div className={styles.messages}>
            {messages.map((m, i) => (
              <div
                key={i}
                className={`${styles.bubble} ${
                  m.role === 'user'
                    ? styles.userBubble
                    : styles.assistantBubble
                }`}
              >
                  <Text variant="body-x-small" color="secondary" as="p">
                    {m.role === 'user' ? (
                      'You'
                    ) : (
                      <>
                        PS Agent
                        {m.latencyMs != null && m.latencyMs >= 0 && (
                          <>
                            {' · '}
                            <span title="Response time (server processing, or round-trip if unavailable)">
                              {formatLatency(m.latencyMs)}
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </Text>
                  {m.role === 'user' ? (
                    <Text
                      variant="body-medium"
                      as="div"
                      className={styles.preWrap}
                    >
                      {m.content}
                    </Text>
                  ) : m.content ? (
                    <>
                      {m.activity && m.activity.length > 0 && (
                        <AgentActivityLog steps={m.activity} compact />
                      )}
                      <MarkdownContent
                        content={m.content}
                        dialect="gfm"
                        linkTarget="_blank"
                      />
                      <AgentDebugTrace stage={m.stage} debug={m.debug} />
                    </>
                  ) : (streamStatus || streamActivity.length > 0) &&
                    loading &&
                    i === messages.length - 1 ? (
                    <>
                      {streamActivity.length > 0 && (
                        <AgentActivityLog steps={streamActivity} />
                      )}
                      {streamStatus && (
                        <Text variant="body-small" color="secondary" as="div">
                          {streamStatus}
                        </Text>
                      )}
                    </>
                  ) : (
                    <Flex alignItems="center" gap="2">
                      <Progress />
                      <Text variant="body-small" color="secondary" as="span">
                        Working…
                      </Text>
                    </Flex>
                  )}
              </div>
            ))}
          </div>
          {loading && (
            <Text variant="body-x-small" color="secondary" as="p" mb="2">
              Waiting… {formatLatency(waitingElapsedMs)}
            </Text>
          )}
          <div className={styles.inputRow}>
            <div className={`${styles.textField} bui-TextField`}>
              <FieldLabel label="Message" />
              <textarea
                className={styles.messageInput}
                rows={2}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
                placeholder="Ask about templates, Terraform defaults, or provisioning…"
                aria-label="Message"
              />
            </div>
            <Button
              variant="primary"
              onPress={() => void send()}
              isDisabled={loading || !input.trim()}
              isPending={loading}
            >
              Send
            </Button>
          </div>
        </div>
      </Content>
    </Page>
  );
};
